import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';
import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';
import { HotelOfferSchema } from '../hotel_planner/hotel_planner_schema.js';
import { HotelAmenitySchema } from '../shared/hotel_amenity_schema.js';
import { HotelPhotoSchema } from '../shared/hotel_photo_schema.js';

const HotelRoomsRateSchema = z.object({
  // 1-based no array flat de quartos da oferta no agents — é o `roomIndex` que a
  // ação `hotel-room-select` ecoa e que o checkout consome (capturePricing[i-1]).
  roomIndex: z.number().int().min(1),
  supplier: z
    .object({
      name: z.string().optional(),
      image: z.string().optional(),
      icon: z.string().optional(),
    })

    .optional(),
  boardDescription: z.string().optional(),
  breakfast: z.boolean().optional(),
  refundable: z.boolean().optional(),
  freeCancellationUntil: z.iso.date().optional(),
  total: ChatUiMoneySchema.optional(),
  totalPerNight: ChatUiMoneySchema.optional(),
  policyStatus: z.enum(['within_policy', 'out_of_policy', 'unknown']).optional(),
  /** As REGRAS de política que ESTA tarifa quebrou (chaves do `HotelPolicies`
   *  do fornecedor, ordem canônica do projetor — igual ao campo homônimo de
   *  `HotelRoomSchema`). O `policyStatus` diz dentro/fora; isto diz POR QUÊ, e
   *  sem ele o card de quartos mostra o veredito sem o motivo que a oferta já
   *  carrega. Evolução ADITIVA: bloco antigo sem o campo segue parseando. */
  policyViolations: z.array(z.string()).max(12).optional(),
  // Tarifa do acordo corporativo (match fail-soft do resume no agents).
  negotiated: z.boolean().optional(),
  quantity: z.number().int().min(0).optional(),
  /** Nome CRU do fornecedor para esta tarifa — é como o viajante audita o
   *  agrupamento canônico antes de reservar. Opcional: parser antigo descarta
   *  por stripping (schema não-strict), nunca rejeita. */
  sourceName: z.string().optional(),
});
export type ChatUiHotelRoomsRate = z.infer<typeof HotelRoomsRateSchema>;

const RoomBedWireSchema = z.object({
  count: z.number().int().min(1).max(9),
  family: z.enum(['SINGLE', 'DOUBLE', 'SOFA']).optional(),
});
const RoomQualifierWireSchema = z.object({
  code: z
    .enum([
      'NO_SMOKING',
      'SMOKING',
      'NO_AC',
      'AC',
      'SEA_VIEW',
      'GARDEN_VIEW',
      'CITY_VIEW',
      'VIEW',
      'BALCONY',
      'PRIVATE',
      'ACCESSIBLE',
      'KITCHEN',
      'FAMILY',
    ])
    .optional(),
  raw: z.string().max(64).optional(),
});
/** Identidade canônica derivada do nome (spec 2026-08-25). Opcional em TODO
 *  campo: bloco antigo não a tem e parser antigo a descarta sem erro. */
const RoomIdentityWireSchema = z.object({
  type: z
    .enum(['SUITE', 'STUDIO', 'APARTAMENTO', 'CHALE', 'BUNGALOW', 'LOFT', 'FLAT', 'CABANA'])
    .optional(),
  tier: z
    .enum([
      'ECONOMICO',
      'STANDARD',
      'CLASSIC',
      'JUNIOR',
      'CONFORT',
      'SUPERIOR',
      'EXECUTIVO',
      'DELUXE',
      'LUXO',
      'PRIME',
      'PREMIUM',
      'MASTER',
    ])
    .optional(),
  tier2: z
    .enum([
      'ECONOMICO',
      'STANDARD',
      'CLASSIC',
      'JUNIOR',
      'CONFORT',
      'SUPERIOR',
      'EXECUTIVO',
      'DELUXE',
      'LUXO',
      'PRIME',
      'PREMIUM',
      'MASTER',
    ])
    .optional(),
  bedrooms: z.number().int().min(1).max(9).optional(),
  capacity: z.number().int().min(1).max(19).optional(),
  beds: z.array(RoomBedWireSchema).max(4).optional(),
  qualifiers: z.array(RoomQualifierWireSchema).max(12).optional(),
});

const HotelRoomsGroupSchema = z.object({
  name: z.string().min(1),
  identity: RoomIdentityWireSchema.optional(),
  /** Nome ilegível/placeholder: o grupo vai para a seção colapsada da UI,
   *  a menos que traga tarifa negociada (`isOtherRoomsGroup`, #5922). */
  unclassified: z.boolean().optional(),
  /** Prosa do quarto (o que o fornecedor escreve sobre a acomodação). Só o
   *  modal de detalhe mostra — no card ela não caberia. */
  about: z.string().optional(),
  photos: z.array(HotelPhotoSchema).max(8).optional(),
  amenities: z.array(HotelAmenitySchema).max(24).optional(),
  /** Toda tarifa do grupo, sem teto: é a lista que o painel do cartão abre
   *  para o viajante comparar fornecedor e regime. O teto de 10 que existia
   *  aqui descartava a cauda do grupo mais concorrido — exatamente onde a
   *  comparação vale mais. */
  rates: z.array(HotelRoomsRateSchema).min(1),
});
export type ChatUiHotelRoomsGroup = z.infer<typeof HotelRoomsGroupSchema>;

/**
 * Membro `hotel_rooms` de `ChatUiSchema` — escolha de quarto pós-seleção de hotel
 * (substitui o room drawer do planner). Grupos por quarto com tarifas por fonte;
 * a seleção dispara `hotel-room-select` (selection estruturada, sem challenge).
 * `viewOnly` congela o card (deck empilhado); `selectedRateRoomIndex` destaca a
 * tarifa escolhida.
 */
export const HotelRoomsBlockSchema = z.object({
  ...chatUiBase,
  kind: z.literal('hotel_rooms'),
  hotel: z.object({
    offerId: z.string().min(1),
    title: z.string().min(1),
    // Nota 0–5; teto 10 só por replay de blocos antigos (ver
    // `intelligence-ui-kit/src/hotel/rating_band.ts`).
    rating: z.number().min(0).max(10).optional(),
    reviewCount: z.number().int().min(0).optional(),
    address: z.string().optional(),
  }),
  /** A oferta escolhida, inteira — o card do hotel que abre o turno de quartos
   *  (a mesma forma do planner, então o card é UM átomo só nas duas telas).
   *  Ausente em bloco antigo e no recibo do checkout: aí o cabeçalho de sempre
   *  vale. `rooms` da oferta não vem — os quartos deste turno são os do bloco. */
  offer: HotelOfferSchema.optional(),
  checkin: z.iso.date().optional(),
  checkout: z.iso.date().optional(),
  nights: z.number().int().min(1).optional(),
  roomsCount: z.number().int().min(1).optional(),
  /** Hóspedes da estadia, sem distinguir idade (ver `HotelRoomOccupancySchema`).
   *  Quem emite hoje preenche este campo. */
  guestsCount: z.number().int().min(1).optional(),
  /** LEGADO, só leitura: o nome deste campo até 2026-08-07. Fica DECLARADO
   *  porque a view ainda o lê (`guestsCount ?? adultsCount`) em bloco antigo do
   *  histórico — sem a declaração, o parse o descartaria e a ocupação sumiria
   *  do replay. Este membro entra numa união discriminada, onde um `preprocess`
   *  não é aceito como opção, então a leitura dupla é o caminho. Nada novo
   *  escreve aqui; sai quando o histórico pré-2026-08-07 decair. */
  adultsCount: z.number().int().min(1).optional(),
  /** Todos os cartões do hotel, sem teto — em PAR com `groupRooms`, que não
   *  trunca (ver o comentário de lá). O teto de 24 descartava grupos em
   *  silêncio, e o excedente não caía nem no porão.
   *  ⚠ FIO: este bloco é persistido em `AppChatMessage.ui` e re-parseado em 3
   *  bordas (app-integration `parse_ui_block.ts`, web-chat live/histórico).
   *  Alargar o teto é seguro para replay (bloco antigo é menor), mas o
   *  CONSUMIDOR tem de aceitar o payload maior ANTES de o produtor emiti-lo:
   *  app-integration/web-chat deployam antes do agents (skew = card vira texto). */
  rooms: z.array(HotelRoomsGroupSchema).min(1),
  selectedRateRoomIndex: z.number().int().min(1).optional(),
  /** Os chips de tarifa que o cartão já abre LIGADOS (#3827): o recorte que o
   *  agente aplicou pelo pedido do viajante ("quero com café"). Só o estado
   *  inicial — a pessoa desliga no chip. Evolução ADITIVA: parser antigo
   *  descarta o campo (schema não-strict) e mostra a lista inteira. */
  rateFilters: z
    .object({
      policy: z.literal(true).optional(),
      breakfast: z.literal(true).optional(),
      refundable: z.literal(true).optional(),
    })
    .optional(),
  viewOnly: z.boolean().optional(),
});
export type HotelRoomsBlock = z.infer<typeof HotelRoomsBlockSchema>;
export type HotelRoomsRateFilters = NonNullable<HotelRoomsBlock['rateFilters']>;
